import { useEffect, useRef, useState } from "react";
import { Seo } from "../components/Seo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import api from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { PartCard } from "../components/parts/PartCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const container = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const suggestionTimer = useRef(null);
  const searchBoxRef = useRef(null);
  const category = searchParams.get("category") || "all";
  const stock = searchParams.get("stock") || "all";
  const search = searchParams.get("search") || "";

  useEffect(() => {
    setLoading(true);
    api
      .get("/parts", { params: { category, stock_status: stock, search: search || undefined } })
      .then((res) => setParts(res.data))
      .catch(() => setParts([]))
      .finally(() => setLoading(false));
  }, [category, stock, search]);

  useEffect(() => {
    const handler = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    updateParam("search", value);

    clearTimeout(suggestionTimer.current);
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestionTimer.current = setTimeout(() => {
      api
        .get("/parts", { params: { search: value } })
        .then((res) => {
          setSuggestions(res.data.slice(0, 6));
          setShowSuggestions(true);
        })
        .catch(() => setSuggestions([]));
    }, 250);
  };

  const selectSuggestion = (part) => {
    setShowSuggestions(false);
    navigate(`/parts/${part.sku}`);
  };

  return (
    <div className="min-h-screen bg-obsidian pt-32 pb-24" data-testid="catalog-page">
      <Seo
        title="Parts Catalog | Orion Services LLC"
        description="Browse Orion Services LLC's full catalog of automotive, truck and tractor parts by category, SKU, and stock availability."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-xs font-mono uppercase tracking-[0.25em] text-amber-light">Parts Catalog</span>
        <h1 className="font-display text-4xl sm:text-5xl uppercase text-white mt-2 mb-6">Full Inventory Index</h1>

        <div className="max-w-3xl mb-10 space-y-4" data-testid="catalog-intro-copy">
          <p className="text-zinc-400 leading-relaxed">
            Every part listed in the Orion Services LLC index carries an exact SKU, a real-time stock status, and a
            documented compatibility record — no guesswork, no vague "fits most models" listings. Our catalog spans
            eight core categories built for commercial duty cycles: engine components, brakes, suspension, filters,
            electrical systems, tires &amp; wheels, transmission assemblies, and hydraulics. Whether you're servicing
            a long-haul Freightliner, a Peterbilt daycab, or a John Deere row-crop tractor, the inventory below reflects
            what is physically on our shelves in Saint Johns, Florida, updated the moment a unit ships or restocks.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            We source both OEM-spec and precision aftermarket components, and every SKU on this page passes internal
            fitment and stress verification before it's cleared for sale. Parts marked <span className="text-emerald-300">in stock</span> ship
            within 2-3 business days; parts marked <span className="text-rose-300">out of stock</span> can usually be quoted with an accurate
            lead-time estimate rather than an open-ended wait. Use the search bar to jump straight to a known SKU or
            part name, or filter by category and availability to narrow the list to exactly what your fleet or field
            equipment needs. If you don't see the exact part you're after, our <a href="/custom-orders" className="text-amber-light underline">custom fabrication team</a> can
            reverse-engineer and machine a replacement from a photo, sketch, or CAD file.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1" ref={searchBoxRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="catalog-search-input"
              placeholder="Search by name, SKU, or compatibility..."
              value={searchInput}
              onChange={handleSearchChange}
              onFocus={() => searchInput.trim() && suggestions.length > 0 && setShowSuggestions(true)}
              autoComplete="off"
              className="pl-10 !bg-carbon border-amber-500/15"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div
                data-testid="catalog-search-suggestions"
                className="absolute z-20 top-full left-0 right-0 mt-2 bg-carbon border border-amber-500/20 rounded-md shadow-xl overflow-hidden"
              >
                {suggestions.map((p) => (
                  <button
                    key={p.sku}
                    type="button"
                    data-testid={`catalog-search-suggestion-${p.sku}`}
                    onClick={() => selectSuggestion(p)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-amber-950/30 transition-colors border-b border-amber-500/10 last:border-b-0"
                  >
                    <span className="text-sm text-white truncate">{p.name}</span>
                    <span className="text-xs font-mono text-amber-light shrink-0">{p.sku}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Select value={category} onValueChange={(v) => updateParam("category", v)}>
            <SelectTrigger data-testid="catalog-category-filter" className="md:w-56 !bg-carbon border-amber-500/15">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stock} onValueChange={(v) => updateParam("stock", v)}>
            <SelectTrigger data-testid="catalog-stock-filter" className="md:w-48 !bg-carbon border-amber-500/15">
              <SelectValue placeholder="Stock Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stock</SelectItem>
              <SelectItem value="in_stock">In Stock</SelectItem>
              <SelectItem value="out_of_stock">Out of Stock</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-zinc-500 font-mono text-sm" data-testid="catalog-loading">Loading parts...</p>
        ) : parts.length === 0 ? (
          <p className="text-zinc-500 font-mono text-sm" data-testid="catalog-empty-state">No parts match your filters.</p>
        ) : (
          <motion.div variants={container} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="catalog-grid">
            {parts.map((p) => (
              <PartCard key={p.sku} part={p} />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
